import React, { useState, useEffect, useMemo } from 'react';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StarIcon from '@mui/icons-material/Star';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { DesktopDatePicker as DatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { useFeathers } from '../../app/util';
import { Application } from '@feathersjs/feathers';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { humanizeDistrictType } from '../../app/util/humanize-district-type';
import moment from 'moment';
import styled from 'styled-components';
import type { CoverageArea } from '../../types/coverage-area';
import type { District } from '../../app/feathers/districts/District';
import type { ProjectDate } from '../../app/feathers/project-dates/ProjectDate';

export interface CoveragePlanSummaryCardProps {
  coverageArea: CoverageArea;
  secondaryCoverageArea?: CoverageArea;
  /** Organization key; null when displaying Branch coverage plan. */
  organizationKey: string | null;
  electionKey?: string;
  canEditProjectDate?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const PAGE_SIZE = 50;
const CHUNK_SIZE = 150;

type DistrictDisplay = Pick<District, '_id' | 'longName'>;

const coveragePlanLabel = (plan?: string) => {
  if (plan === 'auto-coverage') return 'Full profiles';
  if (plan === 'basic-coverage') return 'Candidate lists and metadata';
  if (plan === 'no-coverage') return 'Candidate lists';
  return 'No coverage';
};

/**
 * Card that displays a coverage area summary (readableSummary or fallback).
 * Visually matches CoveragePlanCard. When organizationKey is set, fetches org and shows org name above.
 */
const CoveragePlanSummaryCard = ({
  coverageArea,
  secondaryCoverageArea,
  organizationKey,
  electionKey,
  canEditProjectDate = false,
  className,
  style
}: CoveragePlanSummaryCardProps) => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [orgName, setOrgName] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [districtsById, setDistrictsById] = useState<Record<string, DistrictDisplay>>({});
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [districtLoadError, setDistrictLoadError] = useState<string | null>(null);
  const [projectDate, setProjectDate] = useState<ProjectDate | null>(null);
  const [projectDateLoading, setProjectDateLoading] = useState(false);
  const [projectDateModalOpen, setProjectDateModalOpen] = useState(false);
  const [projectDateDraft, setProjectDateDraft] = useState<moment.Moment | null>(null);
  const [projectDateSaving, setProjectDateSaving] = useState(false);
  const [projectDateError, setProjectDateError] = useState<unknown>(null);

  const supportedAreas = coverageArea.supportedAreas ?? [];
  const priorityArea = coverageArea.priorityArea ?? [];
  const supportedDistrictTypes = coverageArea.supportedDistrictTypes ?? [];
  const showProjectDate = organizationKey !== null;
  const priorityAreaIdSet = useMemo(
    () => new Set(priorityArea.map((id) => `${id}`)),
    [priorityArea]
  );

  const districtIdsForDetails = useMemo(
    () => [...new Set(supportedAreas.map((id) => `${id}`).filter(Boolean))],
    [supportedAreas]
  );

  useEffect(() => {
    if (!feathers || !organizationKey) {
      setOrgName(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const org = await feathers.getService('organizations').get(organizationKey);
        if (!cancelled && org?.name) setOrgName(org.name);
      } catch {
        if (!cancelled) setOrgName(null);
      }
    })();
    return () => { cancelled = true; };
  }, [feathers, organizationKey]);

  useEffect(() => {
    if (!showProjectDate || !feathers || !electionKey) {
      setProjectDate(null);
      return;
    }

    let cancelled = false;
    setProjectDateLoading(true);
    setProjectDateError(null);

    (async () => {
      try {
        const response = await feathers.getService('project-dates').find({
          query: {
            election: electionKey,
            organization: organizationKey ?? null,
            $limit: 1,
          },
        });
        if (cancelled) return;
        setProjectDate(response.data?.[0] ?? null);
      } catch (err) {
        console.error(err);
        if (!cancelled) setProjectDateError(err);
      } finally {
        if (!cancelled) setProjectDateLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [feathers, electionKey, organizationKey, showProjectDate]);

  useEffect(() => {
    if (!detailsOpen || !feathers || districtIdsForDetails.length === 0) return;

    const missingDistrictIds = districtIdsForDetails.filter((id) => !districtsById[id]);
    if (missingDistrictIds.length === 0) return;

    let cancelled = false;
    setLoadingDistricts(true);
    setDistrictLoadError(null);
    loadDistrictsByIdPaginated(feathers, missingDistrictIds)
      .then((loadedDistricts) => {
        if (cancelled) return;
        setDistrictsById((prev) => ({ ...prev, ...loadedDistricts }));
      })
      .catch(() => {
        if (cancelled) return;
        setDistrictLoadError('Unable to load district details.');
      })
      .finally(() => {
        if (!cancelled) setLoadingDistricts(false);
      });

    return () => { cancelled = true; };
  }, [detailsOpen, districtIdsForDetails, districtsById, feathers]);

  const summarizeCoverageArea = (targetCoverageArea: CoverageArea) => {
    const targetSupportedAreas = targetCoverageArea.supportedAreas ?? [];
    const targetSupportedDistrictTypes = targetCoverageArea.supportedDistrictTypes ?? [];
    if (targetCoverageArea.readableSummary) return targetCoverageArea.readableSummary;
    if (targetSupportedAreas.length === 0 && targetSupportedDistrictTypes.length === 0) return 'No plan specified';
    return `${coveragePlanLabel(targetCoverageArea.coveragePlan)} for ${targetSupportedAreas.length} districts and ${targetSupportedDistrictTypes.length} types`;
  };

  const summary = summarizeCoverageArea(coverageArea);
  const secondarySummary = secondaryCoverageArea ? summarizeCoverageArea(secondaryCoverageArea) : null;
  const formattedProjectDate = projectDate?.date
    ? moment(projectDate.date).format('MM/DD/YYYY')
    : null;

  const orgNameModified = organizationKey ? orgName : 'Branch coverage plan';

  const renderDistrictName = (districtId: string) => {
    const district = districtsById[`${districtId}`];
    if (district) return district.longName;
    if (loadingDistricts) return 'Loading...';
    return districtId;
  };

  const openProjectDateModal = () => {
    setProjectDateDraft(projectDate?.date ? moment(projectDate.date) : null);
    setProjectDateError(null);
    setProjectDateModalOpen(true);
  };

  const closeProjectDateModal = () => {
    if (projectDateSaving) return;
    setProjectDateModalOpen(false);
    setProjectDateDraft(null);
    setProjectDateError(null);
  };

  const saveProjectDate = async () => {
    if (!feathers || !electionKey) return;

    setProjectDateSaving(true);
    setProjectDateError(null);
    try {
      const date = projectDateDraft ? moment.utc(projectDateDraft).toISOString() : null;
      const service = feathers.getService('project-dates');
      const savedProjectDate = projectDate?._id
        ? await service.patch(projectDate._id, { date })
        : await service.create({
          election: electionKey,
          ...(organizationKey ? { organization: organizationKey } : {}),
          coverageArea,
          date,
        });
      setProjectDate(savedProjectDate);
      setProjectDateModalOpen(false);
      setProjectDateDraft(null);
    } catch (err) {
      console.error(err);
      setProjectDateError(err);
    } finally {
      setProjectDateSaving(false);
    }
  };

  const projectDateErrorMessage = projectDateError instanceof Error
    ? projectDateError.message
    : 'Unable to save project date';

  return (
    <Card className={className} style={style}>
      {orgNameModified != null && (
        <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 500 }}>
          {orgNameModified}
        </Typography>
      )}
      <div style={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Typography variant='body2'>{summary}</Typography>
          {secondarySummary ? (
            <Typography variant='body2' color='text.secondary'>
              Secondary: {secondarySummary}
            </Typography>
          ) : null}
          {showProjectDate ? (
            <ProjectDateRow>
              {projectDateLoading ? (
                <Typography variant='body2' color='text.secondary'>Loading...</Typography>
              ) : formattedProjectDate ? (
                <ProjectDateValue>
                  <Typography variant='body2'>{formattedProjectDate}</Typography>
                  {canEditProjectDate ? (
                    <IconButton size="small" onClick={openProjectDateModal} title="Edit project date">
                      <EditIcon style={{ fontSize: '16px' }} />
                    </IconButton>
                  ) : null}
                </ProjectDateValue>
              ) : canEditProjectDate ? (
                <Button
                  color="error"
                  size="small"
                  startIcon={<WarningAmberIcon style={{ fontSize: '16px' }} />}
                  onClick={openProjectDateModal}
                  sx={{ alignSelf: 'flex-start', minWidth: 0, p: 0, textTransform: 'none' }}
                >
                  No date set
                </Button>
              ) : (
                <ProjectDateValue>
                  <WarningAmberIcon style={{ fontSize: '16px', color: '#d32f2f' }} />
                  <Typography variant='body2' color='error'>No date set</Typography>
                </ProjectDateValue>
              )}
            </ProjectDateRow>
          ) : null}
        </div>
        <IconButton size="small" onClick={() => setDetailsOpen(true)}>
          <InfoOutlinedIcon style={{ fontSize: '18px'}}/>
        </IconButton>
      </div>

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, pt: 1 }}>
            <Typography variant='body2' color='text.secondary'>
              {orgNameModified}
            </Typography>

            {districtLoadError ? (
              <Typography variant='body2' color='error'>
                {districtLoadError}
              </Typography>
            ) : null}

            <Box>
              <Typography variant='subtitle1' sx={{ fontWeight: 700, mb: 0.5 }}>
                District types
              </Typography>
              {supportedDistrictTypes.length === 0 ? (
                <Typography variant='body2' color='text.secondary'>
                  None
                </Typography>
              ) : (
                <List dense disablePadding>
                  {supportedDistrictTypes.map((districtType) => (
                    <ListItem key={`district-type-${districtType}`} disableGutters sx={{ py: 0.25 }}>
                      <ListItemText primary={humanizeDistrictType(districtType, null)} />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>

            <Divider sx={{ my: 0.25 }} />

            <Box>
              <Typography variant='subtitle1' sx={{ fontWeight: 700, mb: 0.5 }}>
                Supported areas
              </Typography>
              {supportedAreas.length === 0 ? (
                <Typography variant='body2' color='text.secondary'>
                  None
                </Typography>
              ) : (
                <List dense disablePadding>
                  {supportedAreas.map((id) => (
                    <ListItem
                      key={`supported-${id}`}
                      disableGutters
                      sx={{ py: 0.25 }}
                      secondaryAction={
                        priorityAreaIdSet.has(`${id}`) ? (
                          <StarIcon sx={{ fontSize: 16, color: '#ffa726' }} />
                        ) : null
                      }
                    >
                      <ListItemText primary={renderDistrictName(id)} />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>

            {loadingDistricts ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <CircularProgress size={16} />
                <Typography variant='body2' color='text.secondary'>
                  Loading district details...
                </Typography>
              </Box>
            ) : null}
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={projectDateModalOpen} onClose={closeProjectDateModal} maxWidth="xs" fullWidth>
        <DialogTitle>Project date</DialogTitle>
        <DialogContent>
          {projectDateError ? (
            <Alert severity='error' style={{ marginBottom: '16px' }}>
              {projectDateErrorMessage}
            </Alert>
          ) : null}
          <StyledDatePicker
            format="MM/DD/YYYY"
            value={projectDateDraft}
            onChange={setProjectDateDraft}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small',
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeProjectDateModal} disabled={projectDateSaving}>Cancel</Button>
          {projectDate?.date ? (
            <Button onClick={() => setProjectDateDraft(null)} disabled={projectDateSaving}>
              Clear date
            </Button>
          ) : null}
          <Button
            variant="contained"
            onClick={saveProjectDate}
            disabled={projectDateSaving || (!projectDateDraft && !projectDate?.date)}
          >
            {projectDateSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

async function loadDistrictsByIdPaginated(
  feathers: Application<ServiceTypes>,
  ids: string[]
): Promise<Record<string, DistrictDisplay>> {
  if (!ids.length) return {};

  const districtService = feathers.getService('districts');
  const uniqueIds = [...new Set(ids.map((id) => `${id}`).filter(Boolean))];
  const chunks: string[][] = [];
  for (let i = 0; i < uniqueIds.length; i += CHUNK_SIZE) {
    chunks.push(uniqueIds.slice(i, i + CHUNK_SIZE));
  }

  const byId: Record<string, DistrictDisplay> = {};

  await Promise.all(
    chunks.map(async (chunk) => {
      let skip = 0;
      let total = Number.POSITIVE_INFINITY;

      while (skip < total) {
        const response = await districtService.find({
          query: {
            _id: { $in: chunk },
            $limit: PAGE_SIZE,
            $skip: skip,
          },
        });

        const pageData = Array.isArray(response)
          ? response
          : ((response as { data?: District[] }).data ?? []);
        const pageTotal = Array.isArray(response)
          ? pageData.length
          : ((response as { total?: number }).total ?? pageData.length);

        pageData.forEach((district) => {
          byId[`${district._id}`] = { _id: district._id, longName: district.longName };
        });

        if (pageData.length === 0) break;
        total = pageTotal;
        skip += pageData.length;
      }
    })
  );

  return byId;
}

const Card = styled.div`
  padding: 16px;
  background-color: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  border-left: 4px solid #1976d2;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProjectDateRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 4px;
`;

const ProjectDateValue = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
`;

export default CoveragePlanSummaryCard;
